<template>
  <div class="mx-auto my-0 max-w-[1320px]">
    <ClientOnly>
      <div
        v-if="isBasketValueExceedingLimit && alertOpen"
        class="absolute left-0 w-full bg-black py-2 font-semibold text-white"
      >
        <div class="mx-auto flex max-w-[1320px] justify-between px-6">
          <div class="flex items-center gap-4">
            <LazyIconFielmannInfoCircle class="size-5" />
            <span class="text-xs leading-6"> {{ maxValueText }}</span>
          </div>
          <FimButton type="link" no-padding @click="alertOpen = !alertOpen">
            <template #icon>
              <LazyIconFielmannClose class="size-6 text-gray-500" />
            </template>
          </FimButton>
        </div>
      </div>

      <div v-if="!fetching && basketCount === 0" class="mt-8 pt-28 text-center">
        <div>
          <LazyIconFielmannEmojiSad class="mb-5 inline-block size-24" />
          <Headline look="h2" class="mb-4 justify-center" is-uppercase>{{
            $t('basket.no_items_info')
          }}</Headline>
          <p>
            {{ $t('basket.continue_shopping_lenses') }}
          </p>
        </div>

        <div class="mt-6 flex justify-center space-x-4">
          <ReturnToShoppingButton />
        </div>
      </div>
      <div
        v-else-if="loaded"
        class="mx-5 md:grid md:grid-cols-12 md:gap-x-8 md:gap-y-0 lg:gap-x-20"
      >
        <div class="col-span-12 md:col-span-7 lg:col-span-8">
          <div
            class="mb-3.5 mt-5 inline-flex items-baseline justify-start space-x-2 md:mb-6 md:mt-10"
            :class="{
              'mt-44 xs:mt-36 md:mt-24 xl:mt-16':
                isBasketValueExceedingLimit && alertOpen,
            }"
          >
            <Headline
              class="normal-case md:text-3xl"
              weight="normal"
              look="h2"
              >{{ $t('basket.heading') }}</Headline
            >
            <div class="font-normal text-neutral-50">
              ({{ basketCount }} {{ $t('basket.article_count', basketCount) }})
            </div>
          </div>
          <OrderPackageList :items-by-packages="basketItemsByPackages">
            <template #item="{ packageItem }">
              <div class="mt-2">
                <BasketAutomaticDiscountBanner
                  v-if="isAutomaticDiscountType(packageItem.promotion)"
                  :basket-item="packageItem"
                />
                <BasketGiftConditionBanner
                  v-if="isGiftApplicableItem(packageItem)"
                  :basket-item="packageItem"
                />
                <BasketItemPromotionGifts
                  v-if="isGiftApplicableItem(packageItem)"
                  :basket-item="packageItem"
                />
              </div>
              <RxBasketCard
                v-if="getItemGroupId(packageItem)"
                :id="getItemGroupId(packageItem)"
                :basket-item="packageItem"
                :product="packageItem.product"
                :package-item-group="getItemGroup(packageItem)"
                :variant="packageItem.variant"
                :custom-data="packageItem.customData"
                :item-group="getItemGroup(packageItem)!"
                :quantity="packageItem.quantity"
                :is-rx-package-item-unavailable="
                  isRxItemUnavailable(packageItem)
                "
                @click:remove-from-basket="
                  removeRxItemFromBasket({
                    product: packageItem.product,
                    variant: packageItem.variant,
                    itemGroupId: getItemGroupId(packageItem),
                  })
                "
                @click:change-quantity="
                  (quantity: number) => {
                    changeBasketRxProductQuantity(
                      quantity,
                      getItemGroupId(packageItem),
                    )
                  }
                "
                @click:edit-configuration-step="openRxConfigurator"
              />
              <BasketCard
                v-else
                :id="packageItem.variant.id"
                :basket-item="packageItem"
                :product="packageItem.product"
                :variant="packageItem.variant"
                :name="
                  getFirstAttributeLabel(packageItem.product.attributes, 'name')
                "
                :title="
                  getFirstAttributeLabel(packageItem.product.attributes, 'name')
                "
                :display-data="packageItem.displayData"
                :image-hash="getImageHash(packageItem)"
                :unit-price="packageItem.price.unit"
                :quantity="packageItem.quantity"
                :available-quantity="packageItem.availableQuantity"
                :custom-data="packageItem.customData"
                @click:remove-from-basket="removeItemFromBasket(packageItem)"
                @click:change-quantity="
                  (addBasketData: AddBasketData) =>
                    changeBasketProductQuantity({
                      ...addBasketData,
                      customData: packageItem.customData,
                      displayData: {
                        ...(packageItem?.displayData?.['attribute-1'] && {
                          'attribute-1': packageItem.displayData['attribute-1'],
                        }),
                        ...(packageItem?.displayData?.['attribute-2'] && {
                          'attribute-2': packageItem.displayData['attribute-2'],
                        }),
                        ...(packageItem?.displayData?.['attribute-3'] && {
                          'attribute-3': packageItem.displayData['attribute-3'],
                        }),
                      },
                      basketItem: packageItem,
                    })
                "
                @subscription:change="(data: any) => updateSubscription(data)"
                @subscription:stop="(data: any) => stopSubscription(data)"
              />
            </template>
          </OrderPackageList>
        </div>
        <div
          class="col-span-12 mt-8 md:col-span-5 md:mt-[6.5rem] lg:col-span-4"
          :class="{
            'md:mt-20': isBasketValueExceedingLimit && alertOpen,
          }"
        >
          <div class="relative z-10 bg-neutral-10 md:sticky md:top-28">
            <div class="space-y-4 p-6 xl:p-10">
              <PromotionHurryToSaveBanners
                v-if="basketPromotions.hasAppliedPromotions"
              />
              <dl
                class="mb-1 flex flex-wrap justify-between gap-2 text-neutral-90"
              >
                <dt class="w-1/2 grow font-medium text-black">
                  {{ $t('basket.subtotal') }}
                </dt>
                <dd class="text-right font-medium text-black">
                  {{ toCurrency(totalCostWithoutReductions) }}
                </dd>
                <template v-if="totalDiscount">
                  <dt class="w-1/2 grow font-medium text-pricing">
                    {{ $t('basket.discount') }}
                  </dt>
                  <dd class="text-right font-medium text-pricing">
                    {{ withNegativePrefix(toCurrency(totalDiscount)) }}
                  </dd>
                </template>
                <dt class="flex w-1/2 grow">
                  {{ $t('basket.shipping') }}
                  <AsyncTooltip
                    v-if="shippingInfoBoxPdp && !hasRxFreeShipping"
                    :text="shippingInfoBoxPdp"
                  >
                    <LazyIconFielmannInfoCircle
                      class="mb-1 ml-1 inline size-4"
                    />
                  </AsyncTooltip>
                </dt>
                <dd v-if="hasRxFreeShipping" class="text-right">
                  {{ toCurrency(0) }}
                </dd>
                <dd
                  v-else-if="
                    freeShipping && costWithTax < freeShipping.minBasketValue
                  "
                  class="text-right"
                >
                  {{ freeShipping.textMinNotReached }}
                </dd>
                <dd
                  v-else-if="
                    freeShipping &&
                    (costWithTax >= freeShipping.minBasketValue ||
                      hasFreeShippingProduct)
                  "
                  class="text-right"
                >
                  {{ toCurrency(0) }}
                </dd>
                <dd v-else class="text-right">
                  {{ $t('basket.shipping_text_fallback') }}
                </dd>

                <dt class="w-1/2 grow text-black">
                  <span class="font-medium">{{ $t('basket.total') }}</span>
                  {{ $t('basket.including_vat') }}
                </dt>
                <dd class="text-right font-medium text-black">
                  {{ toCurrency(costWithTax) }}
                </dd>
              </dl>
              <FloatingButton>
                <FimButton
                  type="primary"
                  class="flex w-full items-center"
                  :disabled="isCheckoutButtonDisabled"
                  data-testid="go-to-checkout"
                  @click="onClickToCheckoutOrder"
                >
                  <template #icon>
                    <LazyIconFielmannCart class="size-6 text-white" />
                  </template>
                  {{ $t('basket.to_checkout') }}
                </FimButton>
              </FloatingButton>

              <div v-if="containsRxGlassesWithoutPrescriptionValues">
                <p>
                  {{ $t('basket.rx_glasses_without_prescription_values') }}
                </p>
              </div>

              <ProductShippingCostInformation />
            </div>
          </div>
          <div class="mb-6 mt-4 px-6 md:px-0">
            <div class="mb-2 text-base font-medium">
              {{ $t('basket.payment_methods') }}
            </div>
            <PaymentProviders hide-headline class="mb-6" />
          </div>

          <BenefitsList slug="basket" />

          <span class="mt-5 flex justify-end text-[15px] text-gray-600">
            {{ $t('basket.delivery_location_information') }}
          </span>
        </div>
        <FrequentlyBoughtTogetherSlider
          v-if="!containsCareOrContactLens"
          :variant-ids="basketItemVariantIds"
          placement="Cart"
        />
        <FimRecommendations
          v-if="recommendationProducts"
          :recommendation-product="recommendationProduct"
          :product-recommendations="recommendationProducts"
          :title="getRecommendationTitle(recommendationAttr, $i18n)"
          :current-product-category="currentProductCategory"
          class="col-span-full mt-12"
          location="Basket"
        >
          <FimRecommendationLinks
            v-if="recommendationLinkFieldName"
            :field-name="recommendationLinkFieldName"
          />
        </FimRecommendations>
      </div>

      <RxConfiguratorOverlay
        v-if="rxConfiguratorEnabled && rxConfiguratorProps"
        :config="rxConfiguratorProps"
        @add-to-basket="addRxItemToBasket"
        @close="closeRxConfiguratorOverlay"
      />

      <RxEditPrescriptionOverlay
        class="absolute"
        @add-to-basket="updateRxPrescriptionValues"
      />
      <template #fallback>
        <div class="hidden"></div>
      </template>

      <div id="edit-prescription-portal"></div>
    </ClientOnly>
  </div>
</template>

<script setup lang="ts">
import {
  ExistingItemHandling,
  type BasketItemDisplayDataKey,
} from '@scayle/storefront-api'
import type {
  BasketItem,
  BasketItemDisplayData,
  Product,
  Variant,
} from '@scayle/storefront-nuxt'
import type { AddBasketData } from '~/components/basket/card/BasketCardDefault.vue'
import type { RxConfiguratorProps } from '~/components/rx/RxConfigurator/RxConfigurator'
import useUiState from '~/composables/ui/useUiState'
import { useFeatureToggles } from '~/composables/useFeatureToggles'
import { useFimBasket } from '~/composables/useFimBasket'
import { useProductRecommendations } from '~/composables/useProductRecommendations'
import type {
  ConfiguratorSerialized,
  ConfiguratorSerializedAddon,
  ConfiguratorStateValue,
} from '~/composables/useRxConfiguratorState'
import { useRxConfiguratorState } from '~/composables/useRxConfiguratorState'
import { useToast } from '~/composables/useToast'
import { BASKET_WITH } from '~/constants/withParams'
import { Action } from '~/plugins/toast'
import type { IAddOrUpdateItems } from '~/rpcMethods/basket'
import { getFirstAttributeLabel } from '~/utils/attribute'
import { getImageFromList, getImageForVariant } from '~/utils/image'
import { groupItemsByPackages } from '~/utils/order'
import { toCurrency } from '~/utils/price'
import {
  getProductCategory,
  getRecommendationLinkFieldName,
  getRecommendationTitle,
  ProductImageType,
} from '~/utils/product'
import {
  getAddonSerializedItem,
  getItemGroup,
  getItemGroupId,
  getMainSerializedItem,
  isRxMainItem,
  type RXItem,
} from '~/utils/rx'

const { validate: validatePrescriptionValues } = useRxPrescription()
const { logger } = useLogging()
const scope = effectScope()
onScopeDispose(() => scope.stop())

const { $i18n, $fimLocalePath } = useNuxtApp()

const { alert } = useToast()

onBeforeMount(async () => {
  await basket.handleInvalidBasketItems()
})

useSeoMeta({
  title: $i18n.t('basket.seo_title'),
  robots: 'noindex,follow',
})

const basket = useFimBasket({
  with: BASKET_WITH,
})

const getImageHash = (basketItem: FimBasketItem) => {
  const images = removeNonImages(basketItem?.product?.images ?? [])
  const fallbackImage =
    getImageFromList(images, ProductImageType.BUST, 'front') || undefined

  return getImageForVariant(
    images,
    basketItem?.variant?.referenceKey ?? '-',
    fallbackImage,
  )?.hash
}
const loaded = ref(false)
const fetching = computed(() => basket.fetching.value)
const refetching = computed(() => basket.refetching.value)
const router = useRouter()
const { data } = useCustomData({
  options: { immediate: true, dedupe: 'cancel' },
})
const customData = computed(() => data.value)

const {
  trackRemoveFromBasket,
  trackBeginCheckout,
  trackAddToBasket,
  trackBasketItems,
  trackNotification,
} = await useTrackingEvents()

const recommendationProduct = computed(() => basket.items.value[0]?.product)
const recommendationAttr = PRODUCT_RECOMMENDATION_ATTRIBUTE_REF_XSELL

// Update Recommendations in case Products get removed from cart
const productRecommendationRefs = await useProductRecommendations(
  [recommendationAttr],
  'basket',
)

const containsCareOrContactLens = computed(() =>
  basket.items.value.every(
    (item) => isCareProducts(item.product) || isContactLens(item.product),
  ),
)

const basketItemsByPackages = computed(() =>
  groupItemsByPackages(basket.data.value?.packages, basket.items.value),
)

const basketPromotions = await useBasketPromotions()
const { totalCostWithoutReductions, totalDiscount, withNegativePrefix } =
  await useBasketReductions()

const isGiftApplicableItem = ({ product, promotionId }: BasketItem) => {
  if (promotionId) {
    return false
  }
  const id = getFirstAttributeValue(product?.attributes, 'promotion')?.id
  return basketPromotions.allCurrentPromotions.value.some((promotion) => {
    const isFreeGift = isBuyXGetYType(promotion)
    return isFreeGift && promotion.customData?.product?.promotionId === id
  })
}

const removeItemFromBasket = async (item: FimBasketItem | FimOrderItem) => {
  try {
    await basket.removeBasketItem(item as BasketItem<Product, Variant>)
    trackRemoveFromBasket(item.product, item.quantity, item.variant, {
      customData: item.customData,
    })
  } catch (error: unknown) {
    logger.error(error, {
      why: 'basket.removeBasketItem',
      where: 'basket.vue',
      variantId: item.variant.id,
      basketKey: basket.key,
    })
  }
}

const hasFreeShippingProduct = computed(() =>
  basket.items.value.some(
    (item) => !!freeShipping.value?.productIds?.includes(item.product.id),
  ),
)

// Needs to remove the main item as well as items in group
const removeRxItemFromBasket = async ({
  product,
  variant,
  itemGroupId,
}: {
  product: Product
  variant: Variant
  itemGroupId: string
}) => {
  try {
    const deleteItems = basket.rxItemsAndOptions.value.filter(
      (item) => getItemGroupId(item) === itemGroupId,
    )

    await basket.removeBasketItems(deleteItems)

    trackRemoveFromBasket(product, 1, variant)
  } catch (error: unknown) {
    logger.error(error, {
      why: 'basket.removeBasketItems',
      where: 'basket.vue',
      variantId: variant.id,
      itemGroupId,
      basketKey: basket.key,
    })
  }
}

const updateSubscription = (data: {
  subscription: SubscriptionChangeData
  product: Product
  variant: Variant
  basketItem: FimBasketItem
}) => {
  const { subscription, product, basketItem } = data
  const customData = {
    ...basketItem.customData,
    ...getSubscriptionCustomData(subscription),
    productType: getProductType(product),
  }
  const displayDataItem = getSubscriptionDisplayDataItem(subscription, $i18n)

  const displayData = { ...basketItem.displayData }

  if (isContactLens(toValue(product))) {
    displayData['attribute-4' as BasketItemDisplayDataKey] = displayDataItem
  } else {
    displayData['attribute-1'] = displayDataItem
  }
  try {
    basket.addItem({
      variantId: basketItem.variant.id,
      quantity: basketItem.quantity,
      existingItemHandling: ExistingItemHandling.ReplaceExisting,
      displayData,
      customData,
      ...(data.basketItem?.promotionId && {
        promotionId: data.basketItem.promotionId,
      }),
    })
  } catch (error) {
    alert($i18n.t('pdp.basket.error'), Action.confirm)
  }
}

const stopSubscription = (data: {
  subscription: SubscriptionChangeData
  product: Product
  variant: Variant
  basketItem: FimBasketItem
}) => {
  const { basketItem } = data
  try {
    const displayData = { ...basketItem.displayData }

    if (isContactLens(toValue(data.product))) {
      displayData['attribute-4' as BasketItemDisplayDataKey] = {
        key: $i18n.t('basket.displayData.deliveryForecastFallback.key'),
        label: $i18n.t('basket.displayData.deliveryForecastFallback.label'),
        value:
          customData.value?.deliveryEstimatesProductType
            ?.sellableWithoutStockAdditionalTime ??
          $i18n.t('basket.displayData.deliveryForecastFallback.value'),
      }
    } else {
      displayData['attribute-1'] = undefined
    }

    basket.addItem({
      ...basketItem,
      customData: {
        ...basketItem.customData,
        subscription: false,
        pricePromotionKey: undefined,
        subscriptionDefinition: undefined,
        productType: getProductType(data.product),
      },
      existingItemHandling: ExistingItemHandling.ReplaceExisting,
      variantId: basketItem.variant.id,
      displayData,
    })
  } catch (error) {
    alert($i18n.t('pdp.basket.error'), Action.confirm)
  }
}

const changeBasketProductQuantity = ({
  quantity,
  product,
  variant,
  displayData,
  customData,
  basketItem,
}: {
  quantity: number
  product: Product
  variant: Variant
  displayData: BasketItemDisplayData
  customData: any
  basketItem: FimBasketItem
}) => {
  // get current quantity to compute the delta
  const item = basket.items.value.find(
    (item) =>
      item?.product?.id === product?.id && item.variant.id === variant?.id,
  )
  const delta = quantity - (item?.quantity ?? 0)

  basket.addItem({
    variantId: variant.id,
    quantity,
    existingItemHandling: ExistingItemHandling.ReplaceExisting,
    displayData,
    customData,
    ...(basketItem?.promotionId && { promotionId: basketItem.promotionId }),
  })

  if (delta > 0) {
    trackAddToBasket(product, delta, variant, { customData })
  } else if (delta < 0) {
    trackRemoveFromBasket(product, Math.abs(delta), variant, { customData })
  }
}

// Need to increase quantity for the main item in group as well as all
// configuration items
const changeBasketRxProductQuantity = (
  quantity: number,
  itemGroupId: string,
) => {
  const mainItem = basket.items.value.find(
    (item) => getItemGroupId(item) === itemGroupId && isRxMainItem(item),
  )

  if (!mainItem || !mainItem.customData || !mainItem.customData.itemGroup) {
    return
  }

  const mainItemSerialized = getMainSerializedItem(
    mainItem.customData.itemGroup!.id,
    mainItem.variant.id,
    mainItem.customData.shopId!,
    mainItem?.customData?.configurationItems?.map(
      (item) =>
        ({
          product: unref(item.product),
          variant: unref(item.variant),
        }) as ConfiguratorStateValue,
    ) ?? [],
    quantity,
  )

  if (mainItem.customData.prescriptionValues) {
    mainItemSerialized.customData.prescriptionValues =
      mainItem.customData.prescriptionValues
  }

  const configurationItems: ConfiguratorSerializedAddon[] = mainItem.customData
    ?.configurationItems?.length
    ? mainItem?.customData?.configurationItems.map((item) =>
        getAddonSerializedItem(
          mainItem.customData.itemGroup!.id,
          +item.id,
          mainItem.customData.shopId!,
          quantity,
        ),
      )
    : []

  const promotionId = mainItem?.promotionId

  const itemsSerialized: IAddOrUpdateItems['items'] = [
    mainItemSerialized,
    ...configurationItems,
  ].map((item) => {
    const { variantId, quantity, ...params } = item
    return { variantId, quantity, params }
  })
  basket.addOrUpdateItems(
    itemsSerialized,
    {
      existingItemHandling: ExistingItemHandling.ReplaceExisting,
      considerItemGroupForUniqueness: true,
    },
    promotionId,
  )
}

const basketItemVariantIds = computed(() =>
  basket.items.value.map((item) => item.variant.id),
)

const { setErrorState: setRxConfiguratorError, serialize } =
  await useRxConfiguratorState()

const updateRxPrescriptionValues = async ({
  itemGroupId,
}: {
  itemGroupId: string
}) => {
  try {
    basket.fetching.value = true
    const itemsSerialized = await serialize(itemGroupId)
    return await addRxItemToBasket({ itemsSerialized })
  } catch (error) {
    alert($i18n.t('pdp.basket.error'), Action.confirm)
    logger.error(error, {
      why: 'basket.updateRxPrescriptionValues',
      where: 'basket.vue',
      basketKey: basket.key,
    })
  }
}

const addRxItemToBasket = async ({
  itemsSerialized,
}: {
  itemsSerialized: ConfiguratorSerialized
}) => {
  const promotionId = rxConfiguratorProps.value?.basketItemPayload?.promotionId

  try {
    const items: IAddOrUpdateItems['items'] = itemsSerialized.map((item) => {
      const { variantId, quantity, ...params } = item
      return { variantId, quantity, params }
    })

    await basket.addOrUpdateItems(
      items,
      {
        existingItemHandling: ExistingItemHandling.ReplaceExisting,
        considerItemGroupForUniqueness: true,
      },
      promotionId,
    )
    closeRxConfiguratorOverlay()
  } catch (e) {
    setRxConfiguratorError(true)
  }
}

const onClickToCheckoutOrder = async () => {
  try {
    await basket.handleInvalidBasketItems()
    trackBeginCheckout(basket.data.value?.items)
    router.push($fimLocalePath(routeList.checkout))
  } catch (error: any) {
    logger.error(error, {
      why: 'basket.handleInvalidBasketItems',
      where: 'basket.vue',
      basketKey: basket.key,
    })

    alert($i18n.t('pdp.basket.unavailable'), Action.confirm)
  }
}

const maxValueText = computed(() => customData.value?.basketLimit?.maxValueText)

const containsRxGlassesWithoutPrescriptionValues = computed(() => {
  return basket.items.value.some(isRxGlassesWithoutPrescriptionValues)
})

const hasInvalidRxPrescription = computed(() => {
  const isValid = (item: RxPrescriptionValues | undefined) => {
    return (
      item &&
      validatePrescriptionValues(formatPrescriptionToRxConfigurator(item))
    )
  }

  return basket.items.value.some(
    (item) =>
      getItemGroup(item) &&
      isRxMainItem(item) &&
      !hasTypeNoneLens(item) &&
      !isValid(item.customData?.prescriptionValues),
  )
})

// Max basket price should be bellow 700,00€
const isBasketValueExceedingLimit = computed(() => {
  const { basketLimit } = unref(customData) ?? {}

  if (!basketLimit) {
    return false
  }
  const costWithTax = unref(basket.data)?.cost.withTax ?? 0
  return basketLimit?.maxValue < costWithTax
})

const isCheckoutButtonDisabled = computed(
  () =>
    isBasketValueExceedingLimit.value ||
    hasInvalidRxPrescription.value ||
    refetching.value ||
    fetching.value,
)

const isRxItemUnavailable = (item: RXItem) => {
  return !!basket.unavailableRxItems.value.find((it) => {
    return getItemGroupId(it) === getItemGroupId(item)
  })
}

/** RxConfigurator */

// If any of the products in basket supports rxConfigurator enable it
const { isRxConfiguratorEnabled, isRxFreeShippingEnabled } = useFeatureToggles()

const rxConfiguratorEnabled = computed(() =>
  basket.items.value.some((item) => isRxConfiguratorEnabled(item.product)),
)
const rxConfiguratorProps = ref<RxConfiguratorProps>()

const openRxConfigurator = (rxConfProps: RxConfiguratorProps) => {
  rxConfiguratorProps.value = rxConfProps
}

const { closeRxConfiguratorOverlay } = useUiState()

const hasRxItemInBasket = computed(() =>
  basket.items.value?.some((item) => isRxMainItem(item)),
)

const hasRxFreeShipping = computed(
  () => hasRxItemInBasket.value && isRxFreeShippingEnabled(),
)

const recommendationLinkFieldName = computed(() =>
  getRecommendationLinkFieldName(recommendationProduct.value),
)

const recommendationProducts = computed(
  () => productRecommendationRefs.data.value?.[recommendationAttr],
)

const currentProductCategory = computed(() =>
  getProductCategory(recommendationProduct.value),
)

const shippingInfoBoxPdp = computed(
  () => customData.value?.freeShipping?.shippingInfoBoxPdp,
)
const freeShipping = computed(() => customData.value?.freeShipping)
const alertOpen = ref(true)

const basketCount = basket.count
const costWithTax = computed(() => basket.cost.value?.withTax ?? 0)

onMounted(() => {
  if (isBasketValueExceedingLimit.value && maxValueText.value) {
    trackNotification(maxValueText.value)
  }
})

scope.run(() => {
  if (import.meta.server) {
    return
  }

  watch(
    recommendationProduct,
    (value, oldValue) => {
      if (value && value.id !== oldValue?.id) {
        productRecommendationRefs.fetch(value)
      }
    },
    { immediate: true },
  )

  watch(
    basket.items,
    () => {
      trackBasketItems(basket.items.value)
    },
    { once: true },
  )

  watch(basket.items, (next) => {
    loaded.value = loaded.value || Boolean(next)
  })

  watch(isBasketValueExceedingLimit, (isExceedingLimit) => {
    if (isExceedingLimit && maxValueText.value) {
      trackNotification(maxValueText.value)
    }
  })
})

definePageMeta({ pageType: 'basket' })
</script>
